import { useEffect, useState } from "react";
import { ScrollText, Search, RotateCcw, Printer } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ACTIONS = [
  "", "INSPECTION", "SUSPENSION_AUTO", "SUSPENSION_MANUELLE", "REMISE_EN_EXPLOITATION",
  "CLOTURE_ANOMALIE_CRITIQUE", "CLOTURE_ACTION", "CHANGEMENT_RESPONSABLE", "CHANGEMENT_ECHEANCE",
  "SUPPRESSION_UTILISATEUR", "GENERATION_QR", "DEVERROUILLAGE_INSPECTION", "MODIFICATION_INSPECTION",
];
const OBJET_TYPES = ["", "FRONT", "ANOMALIE", "ACTION", "UTILISATEUR", "INSPECTION"];

const ACTION_LABELS = {
  "": "Toutes les actions", INSPECTION: "Inspection", SUSPENSION_AUTO: "Suspension automatique",
  SUSPENSION_MANUELLE: "Suspension manuelle", REMISE_EN_EXPLOITATION: "Remise en exploitation",
  CLOTURE_ANOMALIE_CRITIQUE: "Clôture anomalie critique", CLOTURE_ACTION: "Clôture action",
  CHANGEMENT_RESPONSABLE: "Changement responsable", CHANGEMENT_ECHEANCE: "Changement échéance",
  SUPPRESSION_UTILISATEUR: "Suppression utilisateur", GENERATION_QR: "Génération QR code",
  DEVERROUILLAGE_INSPECTION: "Déverrouillage inspection", MODIFICATION_INSPECTION: "Modification inspection",
};

export default function AdminAuditLog() {
  const { selectedSite } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [objetType, setObjetType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");

  const fetchJournal = () => {
    setLoading(true);
    const params = new URLSearchParams({ site: selectedSite });
    if (action) params.set("action", action);
    if (objetType) params.set("objet_type", objetType);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (search) params.set("search", search);
    api.get(`/admin/journal?${params.toString()}`).then(({ data }) => setEntries(data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchJournal(); }, [selectedSite, action, objetType, dateFrom, dateTo]);

  const resetFilters = () => { setAction(""); setObjetType(""); setDateFrom(""); setDateTo(""); setSearch(""); };

  const filterSummary = [
    action && `Action: ${ACTION_LABELS[action] || action}`,
    objetType && `Type: ${objetType}`,
    dateFrom && `Du ${dateFrom.split("-").reverse().join("/")}`,
    dateTo && `Au ${dateTo.split("-").reverse().join("/")}`,
    search && `Recherche: "${search}"`,
  ].filter(Boolean).join(" · ") || "Aucun filtre (toutes les entrées)";

  return (
    <div className="space-y-4" data-testid="admin-audit-log-panel">
      <div className="rounded-xl border border-slate-800 bg-[#121820] p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 print:hidden">
        <select data-testid="audit-filter-action" value={action} onChange={(e) => setAction(e.target.value)} className="w-full bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100">
          {ACTIONS.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
        <select data-testid="audit-filter-objet-type" value={objetType} onChange={(e) => setObjetType(e.target.value)} className="w-full bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100">
          {OBJET_TYPES.map((o) => <option key={o} value={o}>{o || "Tous les objets"}</option>)}
        </select>
        <input data-testid="audit-filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100" />
        <input data-testid="audit-filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100" />
        <div className="flex gap-2">
          <input
            data-testid="audit-filter-search"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchJournal()}
            className="flex-1 bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100"
          />
          <button data-testid="audit-search-button" onClick={fetchJournal} className="w-10 h-10 shrink-0 rounded-md border border-slate-700 flex items-center justify-center text-slate-300 hover:text-cyan-400 transition-colors">
            <Search className="w-4 h-4" />
          </button>
          <button data-testid="audit-reset-filters" onClick={resetFilters} className="w-10 h-10 shrink-0 rounded-md border border-slate-700 flex items-center justify-center text-slate-300 hover:text-cyan-400 transition-colors">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 print:block">
        <div className="text-xs text-slate-500 font-mono uppercase tracking-widest print:text-black">
          {entries.length} entrée(s) — {filterSummary}
        </div>
        <button
          data-testid="audit-export-pdf-button"
          onClick={() => window.print()}
          className="print:hidden inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
        >
          <Printer className="w-3.5 h-3.5" /> Exporter en PDF
        </button>
      </div>
      <div className="hidden print:block text-xs text-slate-600">
        Journal d'audit — Inspection des Fronts de Carrière — généré le {new Date().toLocaleString("fr-FR")}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Chargement...</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune entrée trouvée.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <div key={e.Audit_ID} data-testid={`audit-log-row-${e.Audit_ID}`} className="rounded-xl border border-slate-800 bg-[#121820] p-4 print:border-black print:bg-white print:text-black break-inside-avoid">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 px-2.5 py-1 print:border-black print:bg-white print:text-black">
                  <ScrollText className="w-3 h-3" /> {ACTION_LABELS[e.Action] || e.Action}
                </span>
                <span className="text-xs text-slate-500 print:text-black">{e.Date_Heure ? new Date(e.Date_Heure).toLocaleString("fr-FR") : ""}</span>
              </div>
              <div className="text-sm text-slate-200 mt-2 print:text-black">
                <b>{e.Utilisateur_Email}</b> · {e.Objet_Type} <span className="font-mono text-slate-500 print:text-black">{e.Objet_ID}</span>
              </div>
              {e.Commentaire && <div className="text-sm text-slate-400 mt-1 print:text-black">{e.Commentaire}</div>}
              {(e.Ancienne_Valeur || e.Nouvelle_Valeur) && (
                <div className="text-xs text-slate-500 mt-1 print:text-black">{e.Ancienne_Valeur} → {e.Nouvelle_Valeur}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
